import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { decode } from '@msgpack/msgpack';
import { StreamService } from './new-tracker/stream.service';

export const scheduleList: any[] = [
    { id: 'daily', value: 'Daily' },
    { id: 'weekly', value: 'Weekly' },
    { id: 'monthly', value: 'Monthly' },
    { id: 'yearly', value: 'Yearly' },
];

export interface ApiResponse<T> {
    success: boolean;
    data: T;
    statusCode: number;
    message: string;
}

export interface GenerateQueryFromChartRequest {
    chartId: number | null;
    question: string | null;
    table: string | null;
    dataSource: string | null;
    period: string | null;
    metricColumns: string[];
    dateColumn: string | null;
    start_date: string | null;
    end_date: string | null;
    trackerType: number | null;
    isExternal: boolean | null;
    selectedItems: { columnName: string; item: string[] }[];
    trackerName: string | null;
}
export type Canvas = {
    id: string;
    name: string;
    trackerCount?: number;
    shared?: boolean;
    owner?: { id: number; name: string; role?: string };
};
export interface CanvasList {
    count: number;
    canvas: Canvas[];
}
export interface CreateCanvasDto {
    name: string;
}
export interface UpdateCanvasDto {
    name: string;
    canvasId: string;
}
export interface AddTrackerToCanvasDto {
    canvasId: string;
    trackerId: string;
}

/** One row of the names-only tracker list. `id` is the tracker eid, not the numeric key. */
export interface TrackerName {
    id: string;
    rank: number;
    name: string;
}

export interface TrackerNameList {
    count: number;
    trackers: TrackerName[];
}

@Injectable({ providedIn: 'root' })
export class NewTrackerService {
    private apiLink = environment.apiLink;
    private _httpClient = inject(HttpClient);

    // UI toggle
    private _toggleSubject = new BehaviorSubject<void>(undefined);
    toggle$ = this._toggleSubject.asObservable();
    emitToggle(): void {
        this._toggleSubject.next();
    }

    // Canvas state via signals
    private _canvasState = signal<{ items: Canvas[]; count: number }>({
        items: [],
        count: 0,
    });
    readonly canvases = computed(() => this._canvasState().items);
    readonly canvasCount = computed(() => this._canvasState().count);

    // Mutation counter to notify listeners (add/edit/delete)
    private _canvasMut = signal(0);
    readonly canvasMutations = computed(() => this._canvasMut());
    private bumpCanvasMut() {
        this._canvasMut.update(v => v + 1);
    }

    constructor(private streamService: StreamService) {}

    // ---- Canvas CRUD ----

    refreshCanvases() {
        return this._httpClient
            .get<ApiResponse<any>>(`${this.apiLink}canvas/get-canvas`)
            .pipe(
                tap((r) => {
                    const d = r?.data ?? {};

                    const owned: Canvas[] = Array.isArray(d.canvas)
                        ? d.canvas.map((c: any) => ({ ...c, shared: false }))
                        : [];

                    const shared: Canvas[] = Array.isArray(d.sharedCanvas)
                        ? d.sharedCanvas.map((c: any) => ({
                              ...c,
                              shared: true,
                          }))
                        : [];

                    const items = [...owned, ...shared];

                    if (owned.length === 0 && shared.length === 0) {
                        this._canvasState.set({ items: [], count: 0 });
                        return;
                    }

                    const count =
                        (Number(d.count) || owned.length) +
                        (Number(d.sharedCount) || shared.length);
                    this._canvasState.set({ items, count });
                })
            );
    }

    getCanvas() {
        return this._httpClient
            .get<ApiResponse<CanvasList>>(`${this.apiLink}canvas/get-canvas`)
            .pipe(map(r => r?.data?.canvas ?? []));
    }

    addCanvas(dto: CreateCanvasDto) {
        return this._httpClient
            .post<ApiResponse<{ canvas: Canvas }>>(
                `${this.apiLink}canvas/create-canvas`,
                dto
            )
            .pipe(
                tap((res) => {
                    const created = res?.data?.canvas as Canvas;
                    if (created) {
                        this._canvasState.update(s => ({
                            items: [created, ...s.items],
                            count: s.count + 1,
                        }));
                        this.bumpCanvasMut();
                    }
                }),
                map(res => res.data.canvas)
            );
    }

    updateCanvas(dto: UpdateCanvasDto) {
        return this._httpClient
            .post<ApiResponse<{ canvas: Canvas }>>(
                `${this.apiLink}canvas/edit-canvas`,
                dto
            )
            .pipe(
                tap((res) => {
                    const updated = res?.data?.canvas as Canvas;
                    if (updated) {
                        this._canvasState.update(s => ({
                            items: s.items.map(c =>
                                c.id === updated.id ? updated : c
                            ),
                            count: s.count,
                        }));
                        this.bumpCanvasMut();
                    }
                }),
                map(res => res.data.canvas)
            );
    }

    deleteCanvas(id: string) {
        return this._httpClient
            .delete<void>(`${this.apiLink}canvas/delete-canvas/${id}`)
            .pipe(
                tap(() => {
                    this._canvasState.update(s => ({
                        items: s.items.filter(c => c.id !== id),
                        count: Math.max(0, s.count - 1),
                    }));
                    this.bumpCanvasMut();
                })
            );
    }

    removeTrackerFromCanvas(modal: { trackerId: string; canvasId: string }) {
        return this._httpClient
            .post<void>(
                `${this.apiLink}canvas/remove-tracker-from-canvas`,
                modal
            )
            .pipe(
                tap(() => {
                    this._canvasState.update(s => ({
                        // keep canvas, just decrement a counter if you have it
                        items: s.items.map(c =>
                            c.id === modal.canvasId
                                ? {
                                      ...c,
                                      trackerCount: Math.max(
                                          0,
                                          (c.trackerCount ?? 1) - 1
                                      ),
                                  }
                                : c
                        ),
                        count: s.count,
                    }));
                    this.bumpCanvasMut(); // notify listeners
                })
            );
    }

    userForSharing() {
        return this._httpClient.get(`${this.apiLink}canvas/users-for-sharing`);
    }
    usersSharedinCanvas(canvasId: any) {
        return this._httpClient.get(`${this.apiLink}canvas/users-shared-in-canvas/${canvasId}`);
    }

    reorderTrackers(modal) {
        return this._httpClient.put<void>(
            `${this.apiLink}tracker/ranks`,
            modal
        );
    }

    reorderTrackersInCanvas(modal) {
        return this._httpClient.post<void>(
            `${this.apiLink}canvas/ranks`,
            modal
        );
    }

    addTrackerToCanvas(dto: AddTrackerToCanvasDto) {
        return this._httpClient.post<void>(
            `${this.apiLink}canvas/add-tracker-to-canvas`,
            dto
        );
    }

    // ---- Trackers & insights ----

    getAllTrackers(
        page: number,
        size: number,
        firstDate?: string,
        lastDate?: string,
        departmentId?: number
    ) {
        let params = new HttpParams()
            .append('offset', page)
            .append('limit', size);
        if (firstDate) params = params.append('start_date', firstDate);
        if (lastDate) params = params.append('end_date', lastDate);
        if (departmentId && departmentId !== 0)
            params = params.append('departmentId', departmentId);

        return this._httpClient
            .get(this.apiLink + 'tracker/get-trackers', {
                params,
                responseType: 'arraybuffer',
                headers: { Accept: 'application/x-msgpack' },
            })
            .pipe(map((buffer: ArrayBuffer) => decode(new Uint8Array(buffer))));
    }

    /**
     * GET /v1/tracker/trackers-name - the cheap tracker list: id, rank and name only.
     *
     * Prefer this over `getAllTrackers` wherever only the identity of a tracker is needed, such as
     * a picker. `getAllTrackers` is msgpack and carries every tracker's whole chart definition
     * along with its data; this route is org-cached plain JSON and carries three fields per row.
     *
     * `id` is the tracker **eid**. The heavier list also exposes a numeric primary key, and a
     * reference stored against that key resolves to a different tracker on another deployment.
     */
    getTrackerNames(): Observable<TrackerNameList> {
        return this._httpClient
            .get<ApiResponse<TrackerNameList>>(
                `${this.apiLink}tracker/trackers-name`
            )
            .pipe(map(res => res?.data ?? { count: 0, trackers: [] }));
    }

    getTrackerData(
        trackerId: string,
        firstDate?: string,
        lastDate?: string,
        modal?: any
    ) {
        let params = new HttpParams();
        if (firstDate) params = params.append('start_date', firstDate);
        if (lastDate) params = params.append('end_date', lastDate);

        const requestBody = modal || {};

        return this._httpClient
            .post(
                this.apiLink + `tracker/get-tracker-data/${trackerId}`,
                requestBody,
                {
                    params,
                    responseType: 'arraybuffer',
                    headers: { Accept: 'application/x-msgpack' },
                }
            )
            .pipe(
                map((buffer: ArrayBuffer) => decode(new Uint8Array(buffer)))
            );
    }

    getTrackerById(
        trackerId: number | string,
        start_date?: string,
        end_date?: string,
        modal?: any
    ) {
        let params = new HttpParams();
        if (start_date) params = params.append('start_date', start_date);
        if (end_date) params = params.append('end_date', end_date);

        const requestBody = modal && Object.keys(modal).length > 0 ? modal : null;
        const url = this.apiLink + `tracker/get-tracker-by-id/${trackerId}`;

        return this._httpClient
            .post(url, requestBody, {
                params,
                responseType: 'arraybuffer',
                headers: { Accept: 'application/x-msgpack' },
            })
            .pipe(
                map((buffer: ArrayBuffer) => {
                    try {
                        return decode(new Uint8Array(buffer));
                    } catch (error) {
                        throw new Error(
                            `Failed to decode MessagePack response: ${(error as Error).message}`
                        );
                    }
                }),
                tap({
                    error: (error) => {
                        console.group('❌ getTrackerById Error');
                        console.error('Status:', error.status);
                        console.error('Status Text:', error.statusText);
                        console.error('URL:', error.url);
                        console.error('Error:', error);

                        // Try to decode error response if it's arraybuffer
                        if (error.error instanceof ArrayBuffer) {
                            try {
                                const errorDecoded = decode(
                                    new Uint8Array(error.error)
                                );
                                console.error('Decoded error:', errorDecoded);
                            } catch (e) {
                                console.error(
                                    'Could not decode error response'
                                );
                                // Try to convert to text
                                try {
                                    const text = new TextDecoder().decode(
                                        error.error
                                    );
                                    console.error('Error as text:', text);
                                } catch (e2) {
                                    console.error(
                                        'Could not convert error to text'
                                    );
                                }
                            }
                        }
                        console.groupEnd();
                    },
                })
            );
    }

    getTrackerDimensionsColumns(trackerId: number | string) {
        return this._httpClient.get(
            this.apiLink + `tracker/get-tracker-dimensions/${trackerId}`
        );
    }

    getMetricasAndDates(modal: any) {
        return this._httpClient
            .post(this.apiLink + 'tracker/get-metrics-and-date', modal)
            .pipe(
                map((res: any) => ({
                    metrics: res.data.metricColumns,
                    dates: res.data.dateColumns,
                    dimensionColumns: res.data.dimensionColumns,
                }))
            );
    }

    addNewTracker(modal: any) {
        return this._httpClient.post(
            this.apiLink + 'tracker/new-tracker',
            modal
        );
    }

    /** POST /v1/tracker/create-from-chart - create a tracker from chat chart payload. */
    createTrackerFromChart(body: Record<string, unknown>) {
        return this._httpClient.post<ApiResponse<unknown>>(
            `${this.apiLink}tracker/create-from-chart`,
            body
        );
    }

    /** POST /v1/tracker/generate-query-from-chart - adapt chart SQL into a tracker query (no persist). */
    generateQueryFromChart(body: GenerateQueryFromChartRequest) {
        return this._httpClient.post<ApiResponse<unknown>>(
            `${this.apiLink}tracker/generate-query-from-chart`,
            body
        );
    }

    generateTrackerInsights(
        id: number | string,
        startDate?: string,
        endDate?: string
    ) {
        let params = new HttpParams();
        if (startDate && endDate) {
            params = params.append('start_date', startDate);
            params = params.append('end_date', endDate);
        }
        return this._httpClient.get(
            this.apiLink +
                `tracker/generate-insights/{trackerId}?trackerId=${id}`,
            { params }
        );
    }

    generateTrackerReport(modal: any) {
        return this._httpClient.post(
            this.apiLink + 'tracker/generate-report',
            modal
        );
    }

    delete(trackerId: number | string) {
        return this._httpClient.delete(
            this.apiLink + `tracker/delete-tracker/${trackerId}`
        );
    }

    shareTracker(body: {
        trackerId: string;
        memberIds: number[];
        staffIds: number[];
        userIds: number[];
    }) {
        return this._httpClient.put(`${this.apiLink}tracker/share-tracker`, body);
    }

    changeTrackerColor(trackerId: number | string, modal: any) {
        return this._httpClient.put(
            this.apiLink + `tracker/update-color-tracker/${trackerId}`,
            modal
        );
    }

    editTracker(trackerId: number, modal: any) {
        return this._httpClient
            .put(this.apiLink + `tracker/edit-tracker/${trackerId}`, modal, {
                responseType: 'arraybuffer',
                headers: { Accept: 'application/x-msgpack' },
            })
            .pipe(
                map((buffer: ArrayBuffer) => {
                    const u8 = new Uint8Array(buffer);
                    return u8.byteLength ? decode(u8) : null;
                })
            );
    }

    getDistinctItems(modal: any) {
        return this._httpClient.post(
            this.apiLink + 'tracker/get-distinct-items',
            modal
        );
    }

    getDistinctItemsFilters(modal: any) {
        return this._httpClient.post(
            this.apiLink + 'data_source/get-distinct-items-filter',
            modal
        );
    }

    generateQuery(modal: any) {
        return this._httpClient.post(
            this.apiLink + 'tracker/generate-query',
            modal
        );
    }

    executeQuery(modal: any) {
        return this._httpClient
            .post(this.apiLink + 'tracker/execute-query', modal, {
                responseType: 'arraybuffer',
                headers: { Accept: 'application/x-msgpack' },
            })
            .pipe(map((buffer: ArrayBuffer) => decode(new Uint8Array(buffer))));
    }

    getInsights(page: number, size: number, type?: string) {
        const params = new HttpParams()
            .append('offset', page)
            .append('limit', size);
        return this._httpClient
            .get(this.apiLink + 'insights/all', { params })
            .pipe(
                map((res: any) => ({
                    data: res.data.insights,
                    count: res.data.count,
                }))
            );
    }

    getInsightData(id: number | string) {
        return this._httpClient
            .get(this.apiLink + `insights/data/${id}`, {
                responseType: 'arraybuffer',
                headers: { Accept: 'application/x-msgpack' },
            })
            .pipe(map((buffer: ArrayBuffer) => decode(new Uint8Array(buffer))));
    }

    incorrectInsight(id: number | string) {
        return this._httpClient.put(
            this.apiLink + `insights/incorrect/${id}`,
            {}
        );
    }

    getMinMaxData(modal: any) {
        return this._httpClient.post(
            this.apiLink + 'tracker/get-min-max-date',
            modal
        );
    }

    shareCanvas(body: {
        canvasId: string;
        memberIds: number[];
        staffIds: number[];
        userIds: number[];
        userId: number;
    }): Observable<any> {
        // replace endpoint path if different on your backend
        return this._httpClient.post(`${this.apiLink}canvas/share`, body);
    }
    getTrackerDataForReport(
        trackerId: number | string,
        start_date?: string,
        end_date?: string,
        modal?: any
    ) {
        let params = new HttpParams();
        if (start_date) params = params.append('start_date', start_date);
        if (end_date) params = params.append('end_date', end_date);

        // Add filters as query params if modal contains selectedItems
        if (modal?.selectedItems && Array.isArray(modal.selectedItems)) {
            params = params.append(
                'filters',
                JSON.stringify(modal.selectedItems)
            );
        }

        const url =
            this.apiLink + `tracker/get-tracker-data-for-report/${trackerId}`;

        // Debug logging
        console.group('🔍 getTrackerDataForReport API Call');
        console.groupEnd();

        return this._httpClient.post(url, { params }).pipe(
            tap({
                next: (response: any) => {
                    console.group('✅ getTrackerDataForReport Response');
                    console.groupEnd();
                },
                error: (error) => {
                    console.group('❌ getTrackerDataForReport Error');
                    console.error('Status:', error.status);
                    console.error('Status Text:', error.statusText);
                    console.error('URL:', error.url);
                    console.error('Error:', error);
                    console.groupEnd();
                },
            })
        );
    }
}
